import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  batchApproveStaging,
  batchRejectStaging,
  listConnections,
  listStagingTransactions,
  type StagingTransaction,
} from "../lib/api-modules/integrations.js";
import {
  Button,
  Card,
  EmptyState,
  Pill,
  SectionHeader,
} from "../components/design-system/index.js";
import { Toast, useToast } from "../components/toast.js";

/**
 * /integrations/staging — Phase 8 Session 2.
 *
 * Supervised-sync inbox. Pending rows grouped by institution. Per-row
 * checkbox + per-row "Reject" link. Bottom toolbar batch-approves /
 * batch-rejects the selected rows. Approved rows are picked up by the
 * StagingPromoteFn stream Lambda, which writes the canonical row +
 * audit row + back-link in one TransactWriteItems.
 */

export function IntegrationsStagingPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<
    Record<string, { connection_id: string; external_tx_id: string }>
  >({});

  const stagingQ = useQuery({
    queryKey: ["staging-transactions", "pending_review"],
    queryFn: () => listStagingTransactions("pending_review"),
  });
  const connectionsQ = useQuery({
    queryKey: ["integrations"],
    queryFn: listConnections,
  });
  const stagingRows = stagingQ.data?.staging_transactions ?? [];
  const connections = connectionsQ.data?.connections ?? [];

  const grouped = useMemo(() => {
    const m = new Map<string, StagingTransaction[]>();
    for (const r of stagingRows) {
      const arr = m.get(r.connection_id) ?? [];
      arr.push(r);
      m.set(r.connection_id, arr);
    }
    return m;
  }, [stagingRows]);

  function nameForConnection(connectionId: string): string {
    return (
      connections.find((c) => c.connection_id === connectionId)
        ?.institution_name ?? `Connection ${connectionId.slice(0, 8)}`
    );
  }

  const approveMutation = useMutation({
    mutationFn: batchApproveStaging,
    onSuccess: (resp) => {
      void queryClient.invalidateQueries({ queryKey: ["staging-transactions"] });
      setSelected({});
      toast.show(`Approved ${resp.updated} (${resp.skipped} skipped)`);
    },
  });
  const rejectMutation = useMutation({
    mutationFn: batchRejectStaging,
    onSuccess: (resp) => {
      void queryClient.invalidateQueries({ queryKey: ["staging-transactions"] });
      setSelected({});
      toast.show(`Rejected ${resp.updated} (${resp.skipped} skipped)`);
    },
  });

  function rowKey(r: StagingTransaction) {
    return `${r.connection_id}:${r.external_tx_id}`;
  }

  function toggle(r: StagingTransaction) {
    const key = rowKey(r);
    setSelected((s) => {
      const next = { ...s };
      if (next[key]) delete next[key];
      else
        next[key] = {
          connection_id: r.connection_id,
          external_tx_id: r.external_tx_id,
        };
      return next;
    });
  }

  function selectAllInGroup(connectionId: string) {
    const rows = grouped.get(connectionId) ?? [];
    setSelected((s) => {
      const next = { ...s };
      for (const r of rows) {
        next[rowKey(r)] = {
          connection_id: r.connection_id,
          external_tx_id: r.external_tx_id,
        };
      }
      return next;
    });
  }

  const selectedIds = Object.values(selected);
  const selectedCount = selectedIds.length;

  return (
    <div className="mx-auto max-w-4xl p-6">
      <SectionHeader
        title="Staging inbox"
        sub="Synced transactions land here for your review. Hearth never auto-merges into the canonical ledger — you approve in batches."
      />

      {stagingQ.isLoading && <p className="text-sm">Loading staging rows...</p>}
      {stagingQ.error && (
        <p className="text-sm text-[var(--color-danger)]">{stagingQ.error.message}</p>
      )}

      {!stagingQ.isLoading && stagingRows.length === 0 ? (
        <EmptyState
          title="No transactions awaiting review"
          description="When Plaid syncs new transactions, they show up here for batch approval."
        />
      ) : (
        <div className="space-y-6">
          {Array.from(grouped.entries()).map(([connectionId, rows]) => (
            <Card key={connectionId}>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-base font-semibold">
                  {nameForConnection(connectionId)}{" "}
                  <Pill tone="neutral">{rows.length} pending</Pill>
                </h3>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={() => selectAllInGroup(connectionId)}
                >
                  Select all in group
                </Button>
              </div>
              <ul className="divide-y divide-[var(--color-border-default)]">
                {rows.map((r) => (
                  <li
                    key={rowKey(r)}
                    className="flex items-center gap-3 py-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(selected[rowKey(r)])}
                      onChange={() => toggle(r)}
                      aria-label={`Select ${r.proposed_transaction.merchant_name}`}
                    />
                    <span className="w-24 shrink-0 font-mono tabular-nums text-xs text-[var(--color-muted)]">
                      {r.proposed_transaction.date ?? ""}
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      {r.proposed_transaction.merchant_name ?? "(no merchant)"}
                    </span>
                    <span className="font-mono tabular-nums text-sm">
                      {formatCents(
                        r.proposed_transaction.amount_cents ?? 0,
                        r.proposed_transaction.direction,
                      )}
                    </span>
                    {r.proposed_transaction.pending && (
                      <Pill tone="warning">pending</Pill>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}

      {selectedCount > 0 && (
        <div className="fixed inset-x-0 bottom-0 border-t border-[var(--color-border-default)] bg-[var(--color-card)] px-4 py-3 shadow-lg">
          <div className="mx-auto flex max-w-4xl items-center justify-between gap-3">
            <p className="text-sm">
              {selectedCount} selected
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={rejectMutation.isPending}
                onClick={() => rejectMutation.mutate(selectedIds)}
              >
                Reject {selectedCount}
              </Button>
              <Button
                type="button"
                disabled={approveMutation.isPending}
                onClick={() => approveMutation.mutate(selectedIds)}
              >
                Approve {selectedCount}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast.message} />
    </div>
  );
}

function formatCents(cents: number, direction: unknown): string {
  const sign = direction === "outflow" ? "-" : "+";
  return `${sign}$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
